<script setup lang="ts">
useSeoMeta({
	title: "会社概要｜株式会社Bang",
	description: "株式会社Bangの会社概要を掲載しています。",

	// OGP設定
	ogTitle: "会社概要｜株式会社Bang",
	ogDescription: "株式会社Bangの会社概要を掲載しています。",
	ogUrl: "https://www.bang-inc.jp/company",
});

const navItems = [{ text: "トップページ", href: "/" }, { text: "会社概要" }];
</script>
<template>
    <main class="company">
        <!-- パンくずリスト -->
        <Breadcrumbs :items="navItems" />
        <div class="company-body">
            <HeadingCorp :level="1">会社概要</HeadingCorp>

            <!-- 会社情報テーブル -->
            <div class="info">
                <dl class="info_list">
                    <div class="info_item info_item-top">
                        <dt class="info_term">会社名</dt>
                        <dd class="info_description">株式会社Bang</dd>
                    </div>
                    <div class="info_item info_item-location">
                        <dt class="info_term">所在地</dt>
                        <dd class="info_description info_description-vertical">
                            <div class="address-text">
                                〒154-0024<br>
                                東京都世田谷区三軒茶屋2丁目11番22号４F
                            </div>
                        </dd>
                    </div>
                    <div class="info_item info_item-map">
                        <dt class="map-wrapper">
                            <iframe
                                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3242.4302803755454!2d139.66663907634165!3d35.641769132139906!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x6018f498c67c8a77%3A0x94385fe28f9b7333!2zQywg77yS5LiB55uuLe-8ke-8kS3vvJLvvJIg5LiJ6LuS6Iy25bGLIOS4lueUsOiwt-WMuiDmnbHkuqzpg70gMTU0LTAwMjQ!5e0!3m2!1sja!2sjp!4v1753252304080!5m2!1sja!2sjp"
                                width="343" height="400" style="border:0;" allowfullscreen="" loading="lazy"
                                referrerpolicy="no-referrer-when-downgrade">
                            </iframe>
                        </dt>
                    </div>
                    <div class="info_item">
                        <dt class="info_term">代表者</dt>
                        <dd class="info_description">代表取締役　長山　真己</dd>
                    </div>
                    <div class="info_item">
                        <dt class="info_term">設立</dt>
                        <dd class="info_description">2021年2月26日</dd>
                    </div>
                    <div class="info_item">
                        <dt class="info_term">資本金</dt>
                        <dd class="info_description">100万円</dd>
                    </div>
                    <div class="info_item">
                        <dt class="info_term non-life">引受保険会社</dt>
                        <dd class="info_description">
                            【損害保険会社】<br>
                            ジェイアイ傷害火災保険株式会社<br>
                            ソニー損害保険株式会社<br>
                            損害保険ジャパン株式会社<br>
                            チューリッヒ保険会社<br>
                            日新火災海上保険株式会社<br>
                            三井住友海上火災保険株式会社
                        </dd>
                    </div>
                    <div class="info_item">
                        <dt class="info_term">取扱い種目</dt>
                        <dd class="info_description">火災保険</dd>
                    </div>
                </dl>
            </div>
        </div>
    </main>
</template>

<style scoped lang="scss">
.company {
    position: relative;
    width: min(91vw, 64.5rem);
    margin: auto;

    // 装飾用の円形背景
    &::before {
        content: '';
        position: absolute;
        width: 20rem; // 320px
        height: 20rem; // 320px
        aspect-ratio: 1/1;
        left: -4rem; // -64px
        top: -3rem; // -48px
        border-radius: 20rem; // 320px
        box-sizing: border-box;
        border: 1.5rem solid #EFF7FF; // 24px
        z-index: -1; // コンテンツの背景に配置
        pointer-events: none; // クリックイベントを通す
    }

    &-body {
        padding: 2rem 0 4.5rem 0;
    }
}

// 会社情報のスタイル
.info_item-top {
    border-top: 1px solid #e5e5e5;
}

.info {
    overflow: hidden;
    color: #252522;
    line-height: 146%;

    &_item {
        display: flex;
        border-bottom: 1px solid #e5e5e5;
        padding: 0.5rem 0;
    }

    &_term {
        width: 28%;
        font-weight: 700;
        display: flex;
        font-size: 0.875rem; // 14px
        margin: 0;
    }

    // 損害保険会社
    .non-life {
        width: 30%;
    }

    &_description {
        display: flex;
        align-items: center;
        font-size: 0.875rem; // 14px

        // 縦並び用のスタイル
        &-vertical {
            flex-direction: column;
            align-items: stretch;
            gap: 1rem;
        }
    }
}

.info_item-location {
    border-bottom: none; // マップ項目は下線なし
}

.map-wrapper {
    border-radius: 1rem;
    overflow: hidden;
    width: 100%;
    display: flex;
    justify-content: center; // 中央寄せ

    iframe {
        border-radius: 1rem; // iframeにも直接適用
        display: block; // インライン要素のマージンを削除
        width: 21.4rem; //343px
        height: 25rem; //400px
    }
}

// PC用のスタイル
@include mixin.media(pc) {
    .company {

        &::before {
            width: 32.5rem; // 520px
            height: 32.5rem; // 520px
            left: -10.7rem; // -171.2px
            top: -4rem; // -64px
            border-radius: 32.5rem; // 520px
            border: 2.5rem solid #EFF7FF; // 24px
        }

        &-body {
            padding: 4rem 0 5rem 0;
        }
    }

    .info {
        dt {
            padding: 0 1rem 0 0.5rem;
        }

        &_item {
            padding: 1rem 0;
        }

        &_term {
            width: 10%;
        }

        // 損害保険会社
        .non-life {
            width: 10%;
        }

        &_item-location {

            .info_description {
                width: 75%;
                padding: 0;
            }
        }
    }

    .info_item-map {
        padding: 0 0 1rem 0 !important; // iframeの余白をなくす
    }

    .map-wrapper {
        padding: unset !important;
        display: flex;
        justify-content: flex-end; // 右寄せ

        iframe {
            width: 57rem; // 912px
            height: 24rem; // 384px
        }
    }
}
</style>